import { useContext, useEffect, useRef, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import api from '../api';
import { resolveMediaUrl } from '../utils/url';
import AvatarImg from '../components/AvatarImg';
import { UserContext } from '../context/UserContext';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Module-level constants/functions to avoid TDZ issues after minification
const LINK_REGEX = /https?:\/\/[^\s)]+/g;
const EMOJI_MAP = {
  ':smile:':'😄', ':laughing:':'😆', ':wink:':'😉', ':heart:':'❤️', ':thumbsup:':'👍', ':fire:':'🔥', ':star:':'⭐', ':100:':'💯'
};
// Emoji grid master list and index at module scope to avoid hook ordering/TDZ
const EMOJI_LIST_CONST = [
  '😀','😁','😂','🤣','😃','😄','😅','😉','😊','😍','😘','😜','🤪','🤗','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥','😭','😡','🤬','🤯','😴','🤤','🤓','😎','🤩','🥳','😇','🤠','😺','😸','😹','😻',
  '👍','👎','👌','🤌','✌️','🤞','🤟','🤘','👏','🙌','👐','🤝','🙏','💪','🫶',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','🔥','✨','⭐','🌟','💥','🎉','🎊','🎁','🎈',
  '🍕','🍔','🍟','🌮','🍣','🍪','🍰','☕','🍺','🍷',
  '⚽','🏀','🎮','🎧','📷','💡','📌','🔗','📎','📝','✅','❌','❗','❓','➡️','⬅️','⬆️','⬇️'
];
const EMOJI_INDEX_CONST = (() => {
  const m = new Map();
  EMOJI_LIST_CONST.forEach(ch => {
    const kw = [];
    if ('😀😁😂🤣😃😄😅😉😊😍😘😜🤪🤗😎🤩🥳😇'.includes(ch)) kw.push('smile','happy','face');
    if ('😭😡🤬🤯😣😥'.includes(ch)) kw.push('sad','angry','cry');
    if ('👍👎👌✌️🤞🤟🤘👏🙌👐🤝🙏💪🫶'.includes(ch)) kw.push('hand','gesture');
    if ('❤️🧡💛💚💙💜🖤🤍🤎💔'.includes(ch)) kw.push('heart','love');
    if ('🔥✨⭐🌟💥'.includes(ch)) kw.push('star','fire','spark');
    if ('🎉🎊🎁🎈'.includes(ch)) kw.push('party','gift','balloon');
    if ('🍕🍔🍟🌮🍣🍪🍰☕🍺🍷'.includes(ch)) kw.push('food','drink');
    if ('⚽🏀🎮🎧'.includes(ch)) kw.push('sport','game','music');
    if ('📷💡📌🔗📎📝'.includes(ch)) kw.push('tool','idea','link','note');
    if ('✅❌❗❓➡️⬅️⬆️⬇️'.includes(ch)) kw.push('mark','arrow','yes','no');
    m.set(ch, kw);
  });
  return m;
})();

function buildMentionedText(text, allUsers) {
  if (!text) return text;
  const map = {};
  (allUsers || []).forEach(u => { if (u && u.username) map[u.username.toLowerCase()] = u.id; });
  return text.replace(/(^|\s)@([a-zA-Z0-9_]{2,30})/g, (full, pre, uname) => {
    const id = map[uname.toLowerCase()];
    if (!id) return full;
    return `${pre}<a href="/u/${id}" class="cc-mention" data-uid="${id}">@${uname}</a>`;
  });
}

function canonicalizeMentionTokens(text, allUsers) {
  if (!text) return text;
  const byId = new Map();
  (allUsers || []).forEach(u => {
    if (!u) return;
    const id = Number(u.id);
    if (!id || isNaN(id)) return;
    const display = (u.displayName || u.username || '').trim();
    if (!display) return;
    byId.set(id, display);
  });

  let out = text;
  // Ensure @everyone token renders consistently
  out = out.replace(/\[[^\]]*\]\(mention:everyone\)/gi, '[@everyone](mention:everyone)');
  // Replace mention label with latest displayName for /u/<id> tokens
  out = out.replace(/\[([^\]]*)\]\((?:https?:\/\/[^)]*)?\/u\/(\d+)\)/g, (full, _label, idStr) => {
    const id = Number(idStr);
    const display = byId.get(id);
    if (!display) return full;
    return `[@${display}](/u/${id})`;
  });
  return out;
}

function applyEmojiShortcodes(text) {
  if (!text) return text;
  return text.replace(/:[a-z0-9_+-]+:/gi, m => EMOJI_MAP[m] || m);
}

function renderChatMarkdown(text, allUsers, currentUserId) {
  const canonical = canonicalizeMentionTokens(text, allUsers);
  const withMentions = buildMentionedText(canonical, allUsers);
  const meId = currentUserId ? Number(currentUserId) : null;

  // Convert mention tokens to HTML anchors so we can style/highlight them reliably.
  // Important: do this after buildMentionedText so we don't regex-rewrite inside <a ...>.
  const withMentionTokensAsHtml = (withMentions || '')
    .replace(/\[@everyone\]\(mention:everyone\)/gi, '<a href="mention:everyone" class="cc-mention cc-mention-everyone">@everyone</a>')
    .replace(/\[([^\]]+)\]\((?:https?:\/\/[^)]*)?\/u\/(\d+)\)/g, (full, label, idStr) => {
      const id = Number(idStr);
      const raw = String(label || '').trim();
      const txt = raw.startsWith('@') ? raw : `@${raw}`;
      const cls = `cc-mention${meId && id === meId ? ' cc-mention-me' : ''}`;
      return `<a href="/u/${id}" class="${cls}">${txt}</a>`;
    });

  const withEmoji = applyEmojiShortcodes(withMentionTokensAsHtml);
  const html = marked.parse(withEmoji, { breaks: true });
  // Hint browser to load/decode images more smoothly; helps reduce layout/scrollbar jitter.
  const htmlWithImgAttrs = html
    .replace(/<img(?![^>]*\bdecoding=)/g, '<img decoding="async"')
    .replace(/<img(?![^>]*\bloading=)/g, '<img loading="lazy"');
  return DOMPurify.sanitize(htmlWithImgAttrs);
}

// Client-side compression fallback for proxies with low body size (e.g., 1MB)
async function compressImageUnder(bytesTarget, file, maxDim = 1920) {
  if (!file || !(file instanceof Blob)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, outW, outH);
    let q = 0.9;
    for (let i = 0; i < 6; i++) {
      const blob = await new Promise(res => canvas.toBlob(res, 'image/webp', q));
      if (!blob) break;
      if (blob.size <= bytesTarget || q <= 0.5) {
        const out = new File([blob], (file.name || 'image') + '.webp', { type: 'image/webp' });
        return out;
      }
      q -= 0.1;
    }
    // fallback last try
    const blob = await new Promise(res => canvas.toBlob(res, 'image/webp', 0.5));
    if (blob) return new File([blob], (file.name || 'image') + '.webp', { type: 'image/webp' });
    return file;
  } catch {
    return file;
  } finally {
    try { URL.revokeObjectURL(url); } catch {}
  }
}

// Einfache Community-Chat Seite: Globaler Nachrichtenstrom für alle eingeloggten Nutzer
// Features:
// - Live-Updates via WebSocket (Event type: community_chat:new)
// - Fallback Polling alle 25s (falls WS ausfällt)
// - Auto-Scroll ans Ende bei neuen Nachrichten
// - Rate-Limit und Validierung werden serverseitig gehandhabt; Client zeigt Fehlermeldung

function EmojiGrid({ filter, onPick, emojiList, emojiIndex }) {
  const f = (filter || '').toLowerCase().trim();
  const items = useMemo(() => {
    if (!f) return emojiList;
    return emojiList.filter(ch => {
      const kws = emojiIndex.get(ch) || [];
      return kws.some(k => k.includes(f));
    });
  }, [f, emojiList, emojiIndex]);
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(8, 1fr)', gap:6 }}>
      {items.map((ch, idx) => (
        <button key={idx} type="button" onClick={() => onPick(ch)}
          style={{ background:'#2a2a2a', border:'1px solid #3a3a3a', borderRadius:8, padding:'6px 0', cursor:'pointer', fontSize:20 }}
          aria-label={`Emoji ${idx}`}
        >{ch}</button>
      ))}
      {items.length === 0 && <div style={{ gridColumn:'1 / -1', fontSize:12, opacity:.6, padding:'6px 2px' }}>Keine Treffer</div>}
    </div>
  );
}

export default function CommunityChat() {
  const { user, subscribeWS } = useContext(UserContext);
  const [messages, setMessages] = useState([]); // [{id,userId,text,createdAt,deletedAt,editedAt,user:{...}}]
  const [allUsers, setAllUsers] = useState([]); // full user list for mention resolution
  const [auditOpen, setAuditOpen] = useState(false);
  const [audits, setAudits] = useState([]);
  const [historyModal, setHistoryModal] = useState({ open:false, messageId:null, edits:[], loading:false, error:'' });
  const [previewCache, setPreviewCache] = useState({}); // url -> preview {status,title,description,imageUrl}
  const [hasOlder, setHasOlder] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [lastEventAt, setLastEventAt] = useState(Date.now());
  const endRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const lastIdRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const nearBottomRef = useRef(true);
  const pendingAutoScrollRef = useRef(false);
  const scrollRafRef = useRef(null);
  const virtualRafRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewWhileAway, setHasNewWhileAway] = useState(false);
  const [hasMentionWhileAway, setHasMentionWhileAway] = useState(false);
  const [mentionUnreadCount, setMentionUnreadCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState(() => {
    try {
      return typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
    } catch {
      return 'unsupported';
    }
  });
  const windowFocusedRef = useRef(true);
  const lastMarkReadAtRef = useRef(0);
  const markingReadRef = useRef(false);

  // Mention picker state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionRangeRef = useRef({ start: 0, end: 0 });
  const lastMentionQueryRef = useRef('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiFilter, setEmojiFilter] = useState('');
  const fileInputRef = useRef(null);
  const [uploads, setUploads] = useState([]); // {id, localUrl, name, size, progress, status:'idle'|'uploading'|'done'|'error', result}
  const [attachPosition, setAttachPosition] = useState('before'); // 'before' | 'after'
  const [lightbox, setLightbox] = useState({ open:false, url:'' });
  useEffect(() => {
    return () => {
      try {
        uploads.forEach(u => { if (u.localUrl) URL.revokeObjectURL(u.localUrl); });
      } catch {}
    };
  }, []);
  const canModerate = useMemo(() => Array.isArray(user?.roles) && (user.roles.includes('moderator') || user.roles.includes('admin') || user.roles.includes('owner')), [user]);
  const cacheBustRef = useRef(Date.now());

  const mentionCandidates = useMemo(() => {
    if (!mentionOpen) return [];
    const q = (mentionQuery || '').toLowerCase().trim();
    const base = (allUsers || [])
      .map(u => ({
        id: u?.id,
        username: u?.username,
        displayName: u?.displayName || u?.username || '',
        avatarUrl: u?.avatarUrl || null
      }))
      .filter(u => u.id && u.displayName);

    let items = base;
    if (q) {
      items = items.filter(u => (u.displayName || '').toLowerCase().startsWith(q));
    }
    items = items.slice(0, 8);

    // Optional: show @everyone only to moderators/admins
    if (canModerate && (!q || 'everyone'.startsWith(q))) {
      items = [{ id: 'everyone', displayName: 'everyone', username: 'everyone', avatarUrl: null }, ...items].slice(0, 8);
    }
    return items;
  }, [mentionOpen, mentionQuery, allUsers, canModerate]);

  // Keep selected index in range when the candidate list changes.
  useEffect(() => {
    if (!mentionOpen) return;
    setMentionIndex(i => {
      const max = Math.max(0, (mentionCandidates?.length || 0) - 1);
      return Math.max(0, Math.min(i, max));
    });
  }, [mentionOpen, mentionCandidates?.length]);

  // Scroll helper
  const scrollToBottom = useCallback((smooth = false) => {
    try {
      if (!endRef.current) return;
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        try {
          endRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
        } catch {}
      });
    } catch {}
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      } catch {}
    };
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (virtualRafRef.current) cancelAnimationFrame(virtualRafRef.current);
      } catch {}
    };
  }, []);

  // Perform at most one auto-scroll after messages changed & DOM updated.
  useLayoutEffect(() => {
    if (!pendingAutoScrollRef.current) return;
    pendingAutoScrollRef.current = false;
    scrollToBottom(false);
  }, [messages.length, scrollToBottom]);

  async function loadInitial() {
    setLoading(true); setError('');
    try {
      const res = await api.get('/community-chat/messages', { params: { limit: 120 } });
      const items = Array.isArray(res.data) ? res.data : [];
      setMessages(items);
      lastIdRef.current = items.length ? items[items.length - 1].id : null;
      if (items.length < 120) setHasOlder(false);
      pendingAutoScrollRef.current = true;
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Nachrichten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadInitial(); }, []);

  // Load user list once for mentions
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/users/list');
        const items = res.data?.items || [];
        if (mounted) setAllUsers(items);
      } catch {}
    })();
    return () => { mounted = false; };
  }, []);

  // Track window focus for browser notifications
  useEffect(() => {
    const onFocus = () => { windowFocusedRef.current = true; };
    const onBlur = () => { windowFocusedRef.current = false; };
    try {
      window.addEventListener('focus', onFocus);
      window.addEventListener('blur', onBlur);
    } catch {}
    return () => {
      try {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('blur', onBlur);
      } catch {}
    };
  }, []);

  // Load unread mention count (persistent)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/community-chat/notifications/unread-count');
        const c = Number(res.data?.count) || 0;
        setMentionUnreadCount(c);
      } catch {}
    })();
  }, []);

  const markMentionsRead = useCallback(async (upToMessageId) => {
    try {
      const now = Date.now();
      if (markingReadRef.current) return;
      if (now - lastMarkReadAtRef.current < 1500) return;
      if (!mentionUnreadCount) return;
      markingReadRef.current = true;
      lastMarkReadAtRef.current = now;
      await api.post('/community-chat/notifications/mark-read', { upToMessageId });
      setMentionUnreadCount(0);
      setHasMentionWhileAway(false);
    } catch {
      // ignore
    } finally {
      markingReadRef.current = false;
    }
  }, [mentionUnreadCount]);

  const requestBrowserNotifications = useCallback(async () => {
    try {
      if (typeof Notification === 'undefined') return;
      const p = await Notification.requestPermission();
      setNotificationPermission(p);
    } catch {
      // ignore
    }
  }, []);

  function notifyMention(title, body) {
    try {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      // Avoid spamming while actively focused
      if (windowFocusedRef.current && !document.hidden) return;
      new Notification(title, { body });
    } catch {}
  }

  const updateMentionFromInput = useCallback((value, cursorPos) => {
    try {
      const pos = typeof cursorPos === 'number' ? cursorPos : (value?.length || 0);
      const upTo = (value || '').slice(0, pos);
      const at = upTo.lastIndexOf('@');
      if (at < 0) {
        setMentionOpen(false);
        lastMentionQueryRef.current = '';
        return;
      }
      // Only trigger if @ is at start or after whitespace, and not inside a markdown token like [@name]
      const prevChar = at > 0 ? upTo[at - 1] : '';
      if (at > 0 && !/\s/.test(prevChar)) {
        setMentionOpen(false);
        lastMentionQueryRef.current = '';
        return;
      }
      if (prevChar === '[') {
        setMentionOpen(false);
        lastMentionQueryRef.current = '';
        return;
      }
      const after = upTo.slice(at + 1);
      if (!after) {
        setMentionOpen(true);
        setMentionQuery('');
        setMentionIndex(0);
        lastMentionQueryRef.current = '';
        mentionRangeRef.current = { start: at, end: pos };
        return;
      }
      if (/\s/.test(after)) {
        setMentionOpen(false);
        lastMentionQueryRef.current = '';
        return;
      }
      if (after.length > 30) {
        setMentionOpen(false);
        lastMentionQueryRef.current = '';
        return;
      }
      setMentionOpen(true);
      setMentionQuery(after);
      if (lastMentionQueryRef.current !== after) {
        setMentionIndex(0);
        lastMentionQueryRef.current = after;
      }
      mentionRangeRef.current = { start: at, end: pos };
    } catch {
      setMentionOpen(false);
      lastMentionQueryRef.current = '';
    }
  }, []);

  const insertMentionToken = useCallback((picked) => {
    try {
      const { start, end } = mentionRangeRef.current || { start: 0, end: 0 };
      const before = (input || '').slice(0, start);
      const after = (input || '').slice(end);
      const isEveryone = picked?.id === 'everyone';
      const label = isEveryone ? 'everyone' : (picked?.displayName || picked?.username || '');
      const token = isEveryone ? `[@everyone](mention:everyone) ` : `[@${label}](/u/${picked.id}) `;
      const next = before + token + after;
      setInput(next);
      setMentionOpen(false);
      setMentionQuery('');
      setMentionIndex(0);
      setTimeout(() => {
        try {
          const el = inputRef.current;
          if (!el) return;
          el.focus();
          const caret = (before + token).length;
          el.setSelectionRange(caret, caret);
        } catch {}
      }, 0);
    } catch {}
  }, [input]);

  const handleInputKeyDown = useCallback((e) => {
    if (!mentionOpen) return;
    if (!mentionCandidates || mentionCandidates.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(i => Math.min(i + 1, mentionCandidates.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(i => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMentionToken(mentionCandidates[mentionIndex] || mentionCandidates[0]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setMentionOpen(false);
      setMentionQuery('');
      setMentionIndex(0);
    }
  }, [mentionOpen, mentionCandidates, mentionIndex, insertMentionToken]);

  // WebSocket subscription
  useEffect(() => {
    if (!subscribeWS) return;
    const unsub = subscribeWS((data) => {
      if (data?.type === 'community_chat:new' && data.message) {
        setMessages(prev => {
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        lastIdRef.current = data.message.id;
        setLastEventAt(Date.now());
        if (nearBottomRef.current) {
          pendingAutoScrollRef.current = true;
        } else {
          setHasNewWhileAway(true);
        }
      } else if (data?.type === 'community_chat:mention' && data.message) {
        // Targeted mention ping
        try {
          setMentionUnreadCount(c => c + 1);
          if (!nearBottomRef.current) setHasMentionWhileAway(true);
          const byName = data?.by?.displayName || data?.by?.username || 'Jemand';
          notifyMention(`${byName} hat dich erwähnt`, (data.message?.text || '').slice(0, 140));
        } catch {}
      } else if (data?.type === 'community_chat:mention_everyone' && data.message) {
        // Everyone ping (mods/admins only on backend)
        try {
          if (data?.by?.id && Number(data.by.id) === Number(user?.id)) return;
          setMentionUnreadCount(c => c + 1);
          if (!nearBottomRef.current) setHasMentionWhileAway(true);
          const byName = data?.by?.displayName || data?.by?.username || 'Jemand';
          notifyMention(`${byName} hat @everyone erwähnt`, (data.message?.text || '').slice(0, 140));
        } catch {}
      } else if (data?.type === 'community_chat:delete' && data.message) {
        setMessages(prev => prev.map(m => m.id === data.message.id ? { ...m, deletedAt: data.message.deletedAt } : m));
      } else if (data?.type === 'community_chat:update' && data.message) {
        setMessages(prev => prev.map(m => m.id === data.message.id ? { ...m, text: data.message.text, editedAt: data.message.editedAt } : m));
      } else if (data?.type === 'community_chat:mute') {
        // optional: could show toast; for now ignore
      }
    });
    return unsub;
  }, [subscribeWS, scrollToBottom, user?.id]);

  // Fallback Polling wenn lange keine WS-Events
  useEffect(() => {
    pollingTimerRef.current && clearInterval(pollingTimerRef.current);
    pollingTimerRef.current = setInterval(async () => {
      // Wenn innerhalb der letzten 20s ein WS Event kam -> kein Poll nötig
      if (Date.now() - lastEventAt < 20000) return;
      try {
        const res = await api.get('/community-chat/messages', { params: { afterId: lastIdRef.current, limit: 120 } });
        const items = Array.isArray(res.data) ? res.data : [];
        if (items.length) {
          setMessages(prev => [...prev, ...items]);
          lastIdRef.current = items[items.length - 1].id;
          if (nearBottomRef.current) {
            pendingAutoScrollRef.current = true;
          } else {
            setHasNewWhileAway(true);
          }
        }
      } catch {}
    }, 25000);
    return () => { if (pollingTimerRef.current) clearInterval(pollingTimerRef.current); };
  }, [lastEventAt, scrollToBottom]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    // Build attachments markdown first to allow sending attachments-only
    const ready = uploads.filter(u => u.status === 'done' && u.result);
    if (!text && ready.length === 0) return;
    setSending(true); setError('');
    // Build attachments markdown with per-upload placement and block separation
    const mdFor = (u) => {
      const thumb = u.result?.thumbApi || u.result?.thumb || u.result?.displayApi || u.result?.display || u.result?.originalApi || u.result?.original;
      const full = u.result?.displayApi || u.result?.display || u.result?.originalApi || u.result?.original || thumb;
      return `[![](${thumb})](${full})`;
    };
    const beforeMd = ready.filter(u => (u.position || 'before') === 'before').map(mdFor).join('\n\n');
    const afterMd = ready.filter(u => (u.position || 'before') === 'after').map(mdFor).join('\n\n');
    const parts = [];
    if (beforeMd) parts.push(beforeMd.trim());
    if (text) parts.push(text.trim());
    if (afterMd) parts.push(afterMd.trim());
    // Use triple newline to force separate paragraphs in Markdown
    const finalText = parts.join('\n\n\n');
    setInput('');
    setEmojiOpen(false);
    setMentionOpen(false);
    setMentionQuery('');
    setMentionIndex(0);
    try {
      const res = await api.post('/community-chat/messages', { text: finalText });
      // Optimistisches Anhängen falls WS verzögert
      setMessages(prev => prev.some(m => m.id === res.data.id) ? prev : [...prev, res.data]);
      lastIdRef.current = res.data.id;
      pendingAutoScrollRef.current = true;
      // clear uploads after successful send
      setUploads(list => {
        list.forEach(u => { if (u.localUrl) { try { URL.revokeObjectURL(u.localUrl); } catch {} } });
        return [];
      });
    } catch (e2) {
      setError(e2?.response?.data?.error || e2?.message || 'Senden fehlgeschlagen.');
      setInput(text); // Eingabe wiederherstellen
    } finally {
      setSending(false);
    }
  };

  async function onPickImage(file) {
    if (!file) return;
    if (file.size > 2*1024*1024) { setError('Bild zu groß (max. 2MB)'); return; }
    const id = Math.random().toString(36).slice(2,9);
    const localUrl = URL.createObjectURL(file);
    setUploads(u => [...u, { id, localUrl, name: file.name, size: file.size, progress: 0, status: 'uploading', position: attachPosition }]);
    let currentFile = file;
    try {
      const uploadOnce = async () => {
        const fd = new FormData();
        fd.append('image', currentFile);
        const { data } = await api.post('/community-chat/upload-image', fd, {
          onUploadProgress: (evt) => {
            if (!evt || !evt.total) return;
            const p = Math.round((evt.loaded / evt.total) * 100);
            setUploads(list => list.map(x => x.id === id ? { ...x, progress: p } : x));
          }
        });
        return data;
      };
      let data = await uploadOnce();
      setUploads(list => list.map(x => x.id === id ? { ...x, status: 'done', result: data } : x));
    } catch (e) {
      if (e?.response?.status === 413) {
        // compress to under 1MB and retry once
        setUploads(list => list.map(x => x.id === id ? { ...x, errorMessage: 'Proxy-Limit 1MB – Bild wird komprimiert…' } : x));
        try {
          currentFile = await compressImageUnder(1000*1000, file, 1920);
          const data = await (async () => {
            const fd = new FormData();
            fd.append('image', currentFile);
            const { data } = await api.post('/community-chat/upload-image', fd, {
              onUploadProgress: (evt) => {
                if (!evt || !evt.total) return;
                const p = Math.round((evt.loaded / evt.total) * 100);
                setUploads(list => list.map(x => x.id === id ? { ...x, progress: p } : x));
              }
            });
            return data;
          })();
          setUploads(list => list.map(x => x.id === id ? { ...x, status: 'done', result: data, errorMessage: undefined } : x));
        } catch (e2) {
          const msg = e2?.response?.data?.error || e2?.message || 'Upload fehlgeschlagen (nach Komprimierung)';
          setUploads(list => list.map(x => x.id === id ? { ...x, status: 'error', errorMessage: msg } : x));
        }
      } else {
        const serverMsg = e?.response?.data?.error || e?.message || 'Upload fehlgeschlagen';
        setUploads(list => list.map(x => x.id === id ? { ...x, status: 'error', errorMessage: serverMsg } : x));
      }
    } finally {
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch {}
    }
  }

  // auto-attach: no manual insert anymore
  function removeUpload(id) {
    setUploads(list => {
      const target = list.find(x => x.id === id);
      if (target?.localUrl) {
        try { URL.revokeObjectURL(target.localUrl); } catch {}
      }
      return list.filter(x => x.id !== id);
    });
  }

  function setUploadPosition(id, pos) {
    setUploads(list => list.map(x => x.id === id ? { ...x, position: pos } : x));
  }

  // Lightbox: delegate clicks on thumb links
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onClick = (e) => {
      const target = e.target;
      if (!target) return;
      const a = target.closest && target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href) return;
      if (href.startsWith('mention:')) {
        e.preventDefault();
        return;
      }
      if (href.includes('/api/uploads/chat-images/') || href.startsWith('/uploads/chat-images/')) {
        e.preventDefault();
        setLightbox({ open:true, url: href });
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [listRef]);

  // Ältere Nachrichten laden (prepend)
  const loadOlder = async () => {
    if (!messages.length || !hasOlder) return;
    const firstId = messages[0].id;
    try {
      const res = await api.get('/community-chat/messages', { params: { beforeId: firstId, limit: 120 } });
      const older = Array.isArray(res.data) ? res.data : [];
      if (older.length === 0) setHasOlder(false);
      if (older.length) {
        setMessages(prev => [...older, ...prev]);
      }
    } catch (e) {
      // ignore
    }
  };

  // Wrapper to call module-level renderer with current users list (function decl to avoid TDZ)
  function renderMarkdown(text) {
    return renderChatMarkdown(text, allUsers, user?.id);
  }

  // Link extraction & preview fetching for visible messages
  useEffect(() => {
    const urls = new Set();
    for (const m of messages) {
      if (m.deletedAt) continue;
      const found = m.text.match(LINK_REGEX);
      if (found) {
        found.slice(0,3).forEach(u => urls.add(u)); // limit per message
      }
    }
    const toFetch = Array.from(urls).filter(u => !previewCache[u]);
    if (!toFetch.length) return;
    (async () => {
      for (const url of toFetch) {
        try {
          setPreviewCache(pc => ({ ...pc, [url]: { status:'loading' } }));
          const res = await api.get('/community-chat/link-preview', { params: { url } });
          setPreviewCache(pc => ({ ...pc, [url]: res.data }));
        } catch (e) {
          setPreviewCache(pc => ({ ...pc, [url]: { status:'error', errorMessage: e?.response?.data?.error || e?.message } }));
        }
      }
    })();
  }, [messages, previewCache]);

  // Fetch audits when panel opened
  useEffect(() => {
    if (!auditOpen || !canModerate) return;
    (async () => {
      try {
        const res = await api.get('/community-chat/audits', { params: { limit: 50 } });
        setAudits(Array.isArray(res.data) ? res.data : res.data?.audits || []);
      } catch {}
    })();
  }, [auditOpen, canModerate]);

  function openHistory(id) {
    setHistoryModal({ open:true, messageId:id, edits:[], loading:true, error:'' });
    (async () => {
      try {
        const res = await api.get(`/community-chat/messages/${id}/edits`);
        setHistoryModal(h => ({ ...h, loading:false, edits: Array.isArray(res.data) ? res.data : res.data?.edits || [] }));
      } catch (e) {
        setHistoryModal(h => ({ ...h, loading:false, error: e?.response?.data?.error || 'Fehler beim Laden.' }));
      }
    })();
  }
  function closeHistory() { setHistoryModal({ open:false, messageId:null, edits:[], loading:false, error:'' }); }

  // Emoji insert helpers
  // Use module-level emoji list/index to avoid TDZ in hooks
  const EMOJI_LIST = EMOJI_LIST_CONST;
  const EMOJI_INDEX = EMOJI_INDEX_CONST;

  function insertEmoji(ch) {
    const el = inputRef.current;
    const s = input;
    if (!el) {
      setInput((s||'') + ch);
      setEmojiOpen(false);
      return;
    }
    try {
      const start = el.selectionStart ?? s.length;
      const end = el.selectionEnd ?? s.length;
      const next = (s.slice(0, start) + ch + s.slice(end));
      setInput(next);
      setEmojiOpen(false);
      // restore caret
      setTimeout(() => {
        try { el.focus(); el.setSelectionRange(start + ch.length, start + ch.length); } catch {}
      }, 10);
    } catch {
      setInput((s||'') + ch);
      setEmojiOpen(false);
    }
  }


  // Moderationsaktionen
  async function deleteMessage(id) {
    try { await api.post(`/community-chat/messages/${id}/delete`); } catch (e) { setError(e?.response?.data?.error || 'Löschen fehlgeschlagen'); }
  }
  async function editMessage(id) {
    const orig = messages.find(m => m.id === id);
    if (!orig || orig.deletedAt) return;
    const neu = prompt('Neuer Text (Markdown erlaubt):', orig.text);
    if (neu == null) return;
    try { await api.post(`/community-chat/messages/${id}/edit`, { text: neu }); } catch (e) { setError(e?.response?.data?.error || 'Bearbeiten fehlgeschlagen'); }
  }
  async function muteUser(userId) {
    const minsStr = prompt('Stumm für Minuten (max 1440):', '10');
    if (minsStr == null) return;
    const minutes = Number(minsStr) || 10;
    try { await api.post('/community-chat/mute', { userId, minutes }); } catch (e) { setError(e?.response?.data?.error || 'Mute fehlgeschlagen'); }
  }

  // moved canModerate above due to dependency usage

  // Virtuelle Anzeige: einfache Fensterung
  const [virtual, setVirtual] = useState({ start:0, end:0, avg:100 });
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (virtualRafRef.current) return;
      virtualRafRef.current = requestAnimationFrame(() => {
        virtualRafRef.current = null;
        const { scrollTop, clientHeight } = el;
        const distanceFromBottom = el.scrollHeight - (scrollTop + clientHeight);
        // Consider the user "at bottom" when close enough.
        const isNear = distanceFromBottom < 140;
        nearBottomRef.current = isNear;
        setIsNearBottom(prev => (prev === isNear ? prev : isNear));
        if (isNear) {
          setHasNewWhileAway(false);
          // Mark mention notifications as read when user is back at bottom
          if (mentionUnreadCount) {
            const upTo = lastIdRef.current;
            if (upTo) markMentionsRead(upTo);
          }
        }

        const avg = virtual.avg || 90;
        const total = messages.length;
        const estIndex = Math.floor(scrollTop / avg);
        const overscan = 18;
        const windowSize = Math.ceil(clientHeight / avg) + (overscan * 2);
        const start = Math.max(0, estIndex - overscan);
        const end = Math.min(total, start + windowSize);
        setVirtual(v => {
          if (v.start === start && v.end === end && v.avg === avg) return v;
          return { ...v, start, end, avg };
        });
      });
    };
    onScroll();
    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      try {
        if (virtualRafRef.current) cancelAnimationFrame(virtualRafRef.current);
      } catch {}
      virtualRafRef.current = null;
    };
  }, [messages.length, virtual.avg, mentionUnreadCount, markMentionsRead]);
  useEffect(() => {
    // Recalculate avg height (sample last 20 rendered items if present)
    const samples = listRef.current?.querySelectorAll('.cc-msg-item');
    if (samples && samples.length) {
      let sum=0,count=0;
      samples.forEach(n=>{ sum+=n.offsetHeight; count++; });
      if (count) setVirtual(v => ({ ...v, avg: Math.min(240, Math.max(60, sum/count)) }));
    }
  }, [messages]);
  const visibleSlice = messages.slice(virtual.start, virtual.end);
  const topSpace = virtual.start * virtual.avg;
  const bottomSpace = (messages.length - virtual.end) * virtual.avg;

  return (
    <>
    {/* Keep global layout as other pages: centered card with margin; only the messages pane scrolls */}
    <div style={{ width:'100vw', minHeight:'100vh', background:'#181818', display:'flex', justifyContent:'center', alignItems:'flex-start' }}>
      <div style={{ maxWidth:960, width:'100%', margin:'40px auto', background:'#222', borderRadius:16, boxShadow:'0 2px 16px #0003', padding:0, display:'flex', flexDirection:'column', position:'relative', left:'-120px' }}>
        <div style={{ padding:'20px 24px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
            <div style={{ minWidth:0 }}>
              <h2 style={{ margin:'0 0 4px', fontSize:26 }}>Community-Chat</h2>
              <div style={{ fontSize:14, opacity:.8 }}>
                Teile kurze Nachrichten mit allen Mitgliedern in Echtzeit. Bleib respektvoll und halte dich an die Netiquette.
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', justifyContent:'flex-end' }}>
              {mentionUnreadCount > 0 && (
                <div
                  title="Ungelesene Erwähnungen"
                  style={{ background:'rgba(50,205,137,0.18)', border:'1px solid rgba(50,205,137,0.35)', color:'#bff2d8', padding:'6px 10px', borderRadius:999, fontSize:12, fontWeight:800 }}
                >
                  Erwähnungen: {mentionUnreadCount}
                </div>
              )}
              {notificationPermission !== 'unsupported' && notificationPermission !== 'granted' && (
                <button
                  type="button"
                  onClick={requestBrowserNotifications}
                  style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'6px 10px', borderRadius:10, cursor:'pointer', fontSize:12 }}
                  title="Browser-Benachrichtigungen aktivieren"
                >
                  Benachrichtigungen aktivieren
                </button>
              )}
            </div>
          </div>
        </div>
          {/* Main content area: message list has its own max-height and scrollbar */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:400, position:'relative' }}>
          <div ref={listRef} style={{ maxHeight:'60vh', minHeight:220, overflowY:'auto', padding:'16px 24px', scrollbarGutter: 'stable', overflowAnchor: 'none' }}>
            {loading && <div style={{ color:'#9aa3b2' }}>Lade Nachrichten …</div>}
            {!loading && error && <div style={{ color:'#ff8484' }}>{error}</div>}
            {!loading && !error && messages.length === 0 && (
              <div style={{ color:'#9aa3b2' }}>Noch keine Nachrichten.</div>
            )}
            {hasOlder && !loading && (
              <button onClick={loadOlder} style={{marginBottom:12, background:'#333', color:'#fff', border:'1px solid #444', borderRadius:8, padding:'6px 10px', cursor:'pointer'}}>
                Ältere laden
              </button>
            )}
            <div style={{ height: topSpace }} />
            {visibleSlice.map(m => (
              <div key={m.id} className="cc-msg-item" style={{ marginBottom:14, display:'flex', alignItems:'flex-start', gap:10, opacity: m.deletedAt ? .5 : 1 }}>
                {(() => {
                  const base = m.user?.avatarUrl ? (m.user.avatarUrl + (m.user.avatarUrl.includes('?') ? '' : (`?t=${cacheBustRef.current}`))) : '/avatars/default-avatar.png';
                  const resolved = resolveMediaUrl(base);
                  return (
                    <AvatarImg src={resolved} alt="Avatar" size={40} style={{ border:'2px solid #32CD89' }} />
                  );
                })()}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:600 }}>{m.user?.displayName || m.user?.username || 'Mitglied'}</span>
                    <span style={{ fontSize:12, opacity:.6 }}>{new Date(m.createdAt).toLocaleString()}</span>
                    {m.editedAt && !m.deletedAt && (
                      <span style={{ fontSize:11, opacity:.55 }}>(bearbeitet{canModerate || m.userId === user?.id ? <button onClick={() => openHistory(m.id)} style={{ marginLeft:4, background:'transparent', color:'#9fbaff', border:'none', cursor:'pointer', fontSize:11, textDecoration:'underline' }}>History</button> : null})</span>
                    )}
                    {m.deletedAt && <span style={{ fontSize:11, color:'#ff8484' }}>(gelöscht)</span>}
                  </div>
                  <div style={{ marginTop:4, fontSize:15, lineHeight:'1.4', wordBreak:'break-word' }}>
                    {m.deletedAt ? (
                      <span style={{ fontStyle:'italic', opacity:.7 }}>Nachricht gelöscht.</span>
                    ) : (
                      <div>
                        <div className="chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                        {/* Link previews */}
                        {(() => {
                          if (m.deletedAt) return null;
                          const links = (m.text.match(LINK_REGEX) || []).slice(0,3);
                          if (!links.length) return null;
                          return links.map(u => {
                            const p = previewCache[u];
                            if (!p) return null;
                            if (p.status === 'loading') return <div key={u} style={{marginTop:6, fontSize:12, opacity:.6}}>Lade Vorschau… ({u})</div>;
                            return (
                              <div key={u} style={{ marginTop:8, background:'#111', border:'1px solid #2d2d2d', borderRadius:10, padding:10, display:'flex', gap:10 }}>
                                {p.imageUrl && <img src={p.imageUrl} alt="Preview" style={{ width:80, height:80, objectFit:'cover', borderRadius:8, flexShrink:0 }} />}
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{p.title || 'Link'}</div>
                                  {p.description && <div style={{ fontSize:12, opacity:.75, maxHeight:90, overflow:'hidden' }}>{p.description}</div>}
                                  <div style={{ fontSize:11, marginTop:4, opacity:.5 }}>{u}</div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                  {canModerate && (
                    <div style={{ marginTop:4, display:'flex', gap:8 }}>
                      {!m.deletedAt && <button onClick={() => deleteMessage(m.id)} style={{ background:'#441f20', color:'#ff8484', border:'1px solid #662d30', padding:'4px 8px', borderRadius:6, cursor:'pointer', fontSize:12 }}>Löschen</button>}
                      {!m.deletedAt && <button onClick={() => editMessage(m.id)} style={{ background:'#1f2744', color:'#9fbaff', border:'1px solid #2f3e66', padding:'4px 8px', borderRadius:6, cursor:'pointer', fontSize:12 }}>Bearbeiten</button>}
                      <button onClick={() => muteUser(m.userId)} style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'4px 8px', borderRadius:6, cursor:'pointer', fontSize:12 }}>Mute Nutzer</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div style={{ height: bottomSpace }} />
            <div ref={endRef} />
          </div>
          {!isNearBottom && (
            <button
              type="button"
              onClick={() => {
                setHasNewWhileAway(false);
                setHasMentionWhileAway(false);
                scrollToBottom(true);
              }}
              style={{
                position: 'absolute',
                left: 24,
                bottom: 92,
                background: '#32CD89',
                color: '#032d1e',
                fontWeight: 800,
                border: '1px solid #32CD89',
                padding: '10px 14px',
                borderRadius: 999,
                cursor: 'pointer',
                boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
                zIndex: 20,
                maxWidth: 'calc(100% - 48px)'
              }}
              aria-label="Zum Ende springen"
              title="Zum Ende"
            >
              {hasMentionWhileAway ? 'Neue Erwähnung – Zum Ende' : (hasNewWhileAway ? 'Neue Nachrichten – Zum Ende' : 'Zum Ende')}
            </button>
          )}
          {/* Input area fixed at bottom of card */}
          <form onSubmit={send} style={{ padding:'12px 24px', borderTop:'1px solid rgba(255,255,255,0.08)', display:'flex', gap:12, flexShrink:0, position:'relative' }}>
            <button
              type="button"
              onClick={() => setEmojiOpen(o => !o)}
              aria-label="Emoji auswählen"
              title="Emoji auswählen"
              style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'0 10px', cursor:'pointer' }}
            >
              <span style={{ fontSize:20, lineHeight:'42px', display:'inline-block' }}>😊</span>
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Datei anhängen"
              title="Datei anhängen"
              style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'0 10px', cursor:'pointer' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display:'block' }}>
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.2a2 2 0 1 1-2.83-2.83l8.49-8.49" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" ref={fileInputRef} style={{ display:'none' }} onChange={(e)=> onPickImage(e.target.files?.[0])} />
            <div style={{ flex:1, position:'relative' }}>
              <input
                type="text"
                placeholder="Nachricht an alle schreiben…"
                value={input}
                onChange={(e) => {
                  const v = e.target.value;
                  setInput(v);
                  updateMentionFromInput(v, e.target.selectionStart);
                }}
                onKeyDown={handleInputKeyDown}
                onKeyUp={(e) => {
                  // Don't re-parse on navigation keys; it would reset the highlighted item.
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') return;
                  updateMentionFromInput(e.target.value, e.target.selectionStart);
                }}
                onClick={(e) => updateMentionFromInput(e.target.value, e.target.selectionStart)}
                maxLength={2000}
                style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'12px 14px', fontSize:15 }}
                autoComplete="off"
                disabled={sending}
                ref={inputRef}
              />
              {mentionOpen && mentionCandidates.length > 0 && (
                <div
                  role="listbox"
                  aria-label="Nutzer erwähnen"
                  style={{ position:'absolute', left:0, right:0, bottom:48, zIndex:60, background:'#1c1c1c', border:'1px solid #333', borderRadius:10, overflow:'hidden', boxShadow:'0 6px 24px rgba(0,0,0,0.4)' }}
                >
                  {mentionCandidates.map((u, idx) => {
                    const active = idx === mentionIndex;
                    const label = u?.id === 'everyone' ? '@everyone' : `@${u.displayName || u.username}`;
                    const avatar = u?.avatarUrl ? resolveMediaUrl(u.avatarUrl) : null;
                    return (
                      <button
                        key={String(u.id) + ':' + idx}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertMentionToken(u);
                        }}
                        onMouseEnter={() => setMentionIndex(idx)}
                        style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background: active ? 'rgba(50,205,137,0.18)' : '#1c1c1c', color:'#fff', border:'none', cursor:'pointer' }}
                      >
                        {u?.id === 'everyone' ? (
                          <div style={{ width:28, height:28, borderRadius:999, background:'#2a2a2a', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, color:'#bff2d8' }}>@</div>
                        ) : (
                          <AvatarImg src={avatar || '/avatars/default-avatar.png'} alt="Avatar" size={28} />
                        )}
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontWeight:800, fontSize:13 }}>{label}</div>
                          {u?.id !== 'everyone' && u?.username && (
                            <div style={{ fontSize:11, opacity:.7, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.username}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button type="submit" disabled={sending || (!input.trim() && uploads.filter(u=>u.status==='done' && u.result).length===0)} style={{ background:'#32CD89', color:'#032d1e', fontWeight:700, border:'1px solid #32CD89', padding:'12px 18px', borderRadius:10, cursor:'pointer', minWidth:140 }}>
              {sending ? 'Senden…' : 'Senden'}
            </button>

            {emojiOpen && (
              <div
                style={{ position:'absolute', bottom:58, left:16, zIndex:50, background:'#1c1c1c', border:'1px solid #333', borderRadius:10, padding:10, width:320, maxHeight:320, overflow:'hidden', boxShadow:'0 6px 24px rgba(0,0,0,0.4)' }}
                role="dialog"
                aria-label="Emoji-Auswahl"
              >
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <input
                    type="text"
                    placeholder="Emoji suchen…"
                    value={emojiFilter}
                    onChange={(e)=> setEmojiFilter(e.target.value)}
                    style={{ flex:1, background:'#111', color:'#fff', border:'1px solid #333', borderRadius:8, padding:'8px 10px', fontSize:13 }}
                  />
                  <button type="button" onClick={()=> setEmojiOpen(false)} style={{ background:'#333', color:'#fff', border:'1px solid #444', borderRadius:8, padding:'8px 10px', cursor:'pointer', fontSize:12 }}>Schließen</button>
                </div>
                <div style={{ overflowY:'auto', maxHeight:260 }}>
                  <EmojiGrid filter={emojiFilter} onPick={(ch)=> insertEmoji(ch)} emojiList={EMOJI_LIST} emojiIndex={EMOJI_INDEX} />
                </div>
              </div>
            )}
          </form>
          <div style={{ fontSize:11, opacity:.5, padding:'0 24px 10px', flexShrink:0 }}>Max. 10 Nachrichten / Minute · Länge bis 2000 Zeichen · Markdown & Emoji erlaubt · Updates in Echtzeit</div>
          {/* Pending uploads preview + attach position */}
          {uploads.length > 0 && (
            <div style={{ padding:'0 24px 16px' }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
              {uploads.map(u => (
                <div key={u.id} style={{ background:'#1a1a1a', border:'1px solid #333', borderRadius:10, padding:10, width:220 }}>
                  <div style={{ width:'100%', height:120, background:'#111', borderRadius:8, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {u.localUrl ? <img src={u.localUrl} alt={u.name} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} /> : <div style={{ color:'#888', fontSize:12 }}>Vorschau</div>}
                  </div>
                  <div style={{ fontSize:12, marginTop:8, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.name}</div>
                  <div style={{ fontSize:11, opacity:.7 }}>{Math.round(u.size/1024)} KB</div>
                  <div style={{ marginTop:8, height:6, background:'#2d2d2d', borderRadius:4, overflow:'hidden' }}>
                    <div style={{ width:`${u.progress|| (u.status==='done'?100:0)}%`, height:'100%', background: u.status==='error' ? '#ff6b6b' : '#32CD89' }} />
                  </div>
                  <div style={{ marginTop:8, display:'flex', gap:8, alignItems:'center' }}>
                    <span style={{ fontSize:12, color: u.status==='done' ? '#9fe7b2' : (u.status==='error' ? '#ff9a9a' : '#bbb') }}>
                      {u.status==='done' ? 'Wird angehängt' : (u.status==='error' ? (u.errorMessage || 'Fehler beim Upload') : 'Lade…')}
                    </span>
                    <button type="button" onClick={()=>removeUpload(u.id)} style={{ marginLeft:'auto', background:'#3a2a2a', color:'#ffb3b3', border:'1px solid #5a3a3a', borderRadius:8, padding:'6px 8px', fontSize:12, cursor:'pointer' }}>Entfernen</button>
                  </div>
                  {u.status==='error' && u.errorMessage && (
                    <div style={{ marginTop:6, fontSize:11, color:'#ff9a9a' }}>{u.errorMessage}</div>
                  )}
                  {u.status==='done' && (
                    <div style={{ marginTop:8 }}>
                      <div style={{ fontSize:12, opacity:.75, marginBottom:4 }}>Relativ zum Text:</div>
                      <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                        <input type="radio" name={`pos-${u.id}`} checked={(u.position||'before')==='before'} onChange={()=> setUploadPosition(u.id, 'before')} /> darüber <span style={{opacity:.6}}>(Text unter dem Bild)</span>
                      </label>
                      <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
                        <input type="radio" name={`pos-${u.id}`} checked={(u.position||'before')==='after'} onChange={()=> setUploadPosition(u.id, 'after')} /> darunter <span style={{opacity:.6}}>(Text über dem Bild)</span>
                      </label>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          )}
          {/* Audit Log Panel Toggle */}
          {canModerate && (
            <div style={{ padding:'0 24px 24px', flexShrink:0 }}>
              <button onClick={() => setAuditOpen(o => !o)} style={{ background:auditOpen ? '#3d2f55' : '#2f2f2f', color:'#d5c9ff', border:'1px solid #4a3d66', padding:'6px 14px', borderRadius:8, cursor:'pointer', fontSize:12 }}>
                {auditOpen ? 'Moderations-Logs schließen' : 'Moderations-Logs anzeigen'}
              </button>
              {auditOpen && (
                <div style={{ marginTop:12, maxHeight:260, overflowY:'auto', background:'#181818', border:'1px solid #333', borderRadius:10, padding:12 }}>
                  {audits.length === 0 && <div style={{ fontSize:12, opacity:.6 }}>Keine Log-Einträge.</div>}
                  {audits.map(a => (
                    <div key={a.id} style={{ fontSize:12, padding:'6px 8px', borderBottom:'1px solid #2a2a2a' }}>
                      <strong>{a.action}</strong> · Actor: {a.actorUserId} · Msg: {a.targetMessageId || '–'} · Zeit: {new Date(a.createdAt).toLocaleString()}
                      {a.meta && <div style={{ opacity:.65, marginTop:2 }}>{JSON.stringify(a.meta)}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    {historyModal.open && (
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
        <div style={{ background:'#222', border:'1px solid #444', borderRadius:12, width:'min(640px, 92vw)', maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #333', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ margin:0, fontSize:18 }}>Edit-Historie Nachricht #{historyModal.messageId}</h3>
            <button onClick={closeHistory} style={{ background:'#333', color:'#fff', border:'1px solid #444', padding:'4px 10px', borderRadius:6, cursor:'pointer', fontSize:12 }}>Schließen</button>
          </div>
          <div style={{ padding:12, overflowY:'auto' }}>
            {historyModal.loading && <div style={{ fontSize:13, opacity:.7 }}>Lade…</div>}
            {historyModal.error && <div style={{ fontSize:13, color:'#ff8484' }}>{historyModal.error}</div>}
            {!historyModal.loading && !historyModal.error && historyModal.edits.length === 0 && <div style={{ fontSize:13, opacity:.6 }}>Keine Bearbeitungen gefunden.</div>}
            {historyModal.edits.map(e => (
              <div key={e.id} style={{ marginBottom:14, background:'#181818', border:'1px solid #2d2d2d', borderRadius:8, padding:10 }}>
                <div style={{ fontSize:11, opacity:.6 }}>Bearbeitet: {new Date(e.createdAt).toLocaleString()} · Editor: {e.editorUserId}</div>
                <div style={{ display:'flex', gap:10, marginTop:6 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, opacity:.5, marginBottom:2 }}>Vorher</div>
                    <div style={{ fontSize:12, whiteSpace:'pre-wrap' }}>{e.previousText}</div>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, opacity:.5, marginBottom:2 }}>Neu</div>
                    <div style={{ fontSize:12, whiteSpace:'pre-wrap' }}>{e.newText}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
    {lightbox.open && (
      <div onClick={() => setLightbox({ open:false, url:'' })} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
        <div onClick={(e)=> e.stopPropagation()} style={{ maxWidth:'92vw', maxHeight:'88vh', borderRadius:10, overflow:'hidden', border:'1px solid #444', background:'#111', padding:10 }}>
          <img src={lightbox.url} alt="Bild" style={{ maxWidth:'90vw', maxHeight:'85vh', display:'block' }} />
        </div>
        <button onClick={() => setLightbox({ open:false, url:'' })} style={{ position:'fixed', top:20, right:20, background:'#333', color:'#fff', border:'1px solid #444', borderRadius:8, padding:'8px 12px', cursor:'pointer' }}>Schließen</button>
      </div>
    )}
    </>
  );
}

// Minimal CSS injection for mention styling (only once)
if (typeof document !== 'undefined' && !document.getElementById('cc-mention-style')) {
  const style = document.createElement('style');
  style.id = 'cc-mention-style';
  style.textContent = `.cc-mention{color:#32CD89;font-weight:700;text-decoration:none;} .cc-mention:hover{text-decoration:underline;} .cc-mention-me{background:rgba(50,205,137,0.16);border:1px solid rgba(50,205,137,0.25);padding:1px 6px;border-radius:999px;} .cc-mention-everyone{background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.14);padding:1px 6px;border-radius:999px;}`;
  document.head.appendChild(style);
}

// Ensure chat images render as block elements in markdown
if (typeof document !== 'undefined' && !document.getElementById('cc-chat-md-style')) {
  const style2 = document.createElement('style');
  style2.id = 'cc-chat-md-style';
  style2.textContent = `.chat-md img{display:block;max-width:100%;width:min(100%,520px);height:auto;min-height:140px;max-height:420px;object-fit:contain;margin:8px 0;border-radius:10px;background:rgba(255,255,255,0.04);} .chat-md a{display:inline-block;}`;
  document.head.appendChild(style2);
}